import { baseApi } from "../baseApi";
import type { DataGridPagination } from "@/sharedTypes";

// Google Text-to-Speech and Translate go through the gateway, which holds the
// API key and logs every synthesis with its cost for the super admin report.

const ttsUsageTag = "TtsUsage";

export type GoogleVoice = {
  name: string;
  languageCodes: string[];
  ssmlGender: "MALE" | "FEMALE" | "NEUTRAL" | "SSML_VOICE_GENDER_UNSPECIFIED";
  naturalSampleRateHertz: number;
};

/** LINEAR16 is uncompressed PCM, which Google returns as a complete WAV file. */
export type TtsAudioEncoding = "MP3" | "LINEAR16";

export interface SynthesizeRequest {
  text: string;
  languageCode: string;
  voiceName: string;
  speakingRate?: number;
  pitch?: number;
  audioEncoding?: TtsAudioEncoding;
}

export interface TranslateRequest {
  text: string;
  target: string;
}

export interface TranslateResult {
  translatedText: string;
  detectedSourceLanguage: string;
}

export interface TtsUsageBucket {
  requests: number;
  failed_requests: number;
  chars: number;
  cost_usd: number;
}

export interface TtsUsageSummary {
  time_zone: string;
  totals: TtsUsageBucket & { unpriced_chars: number };
  by_company: (TtsUsageBucket & { company_id: number | null; company_name: string | null })[];
  by_tier: (TtsUsageBucket & { voice_tier: string })[];
  by_day: (TtsUsageBucket & { date: string })[];
}

export interface TtsUsageLogEntry {
  id: number;
  company_id: number | null;
  company_name: string | null;
  agent_id: number | null;
  agent_username: string | null;
  voice_name: string;
  language_code: string;
  voice_tier: string;
  audio_encoding: TtsAudioEncoding | string;
  char_count: number;
  price_per_million_usd: number | null;
  cost_usd: number | null;
  status: "success" | "error";
  http_status: number | null;
  error_message: string | null;
  duration_ms: number | null;
  createdAt: string;
}

export interface TtsUsageQuery {
  start_date?: string;
  end_date?: string;
  /** A company id, or "none" for super admin usage (no company). */
  company_id?: number | "none";
  status?: "success" | "error";
  voice_tier?: string;
}

export interface TtsUsageLogsQuery extends TtsUsageQuery {
  page?: number;
  pageSize?: number;
}

export interface TtsUsageLogsResponse {
  items: TtsUsageLogEntry[];
  pagination: DataGridPagination;
}

const clean = (params: object): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  });
  return out;
};

export const ttsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTtsVoices: builder.query<GoogleVoice[], void>({
      query: () => "/tts/voices",
      transformResponse: (response: { voices?: GoogleVoice[] }) => response?.voices ?? [],
    }),
    synthesizeSpeech: builder.mutation<
      { audioContent: string; audioEncoding: TtsAudioEncoding },
      SynthesizeRequest
    >({
      query: (body) => ({ url: "/tts/synthesize", method: "POST", body }),
      invalidatesTags: [ttsUsageTag],
    }),
    translateText: builder.mutation<TranslateResult, TranslateRequest>({
      query: (body) => ({ url: "/translate", method: "POST", body }),
    }),
    getTtsUsageSummary: builder.query<TtsUsageSummary, TtsUsageQuery>({
      query: (params) => ({ url: "/tts/usage/summary", params: clean(params) }),
      providesTags: [ttsUsageTag],
    }),
    getTtsUsageLogs: builder.query<TtsUsageLogsResponse, TtsUsageLogsQuery>({
      query: (params) => ({ url: "/tts/usage/logs", params: clean(params) }),
      providesTags: [ttsUsageTag],
    }),
  }),
});

export const {
  useGetTtsVoicesQuery,
  useSynthesizeSpeechMutation,
  useTranslateTextMutation,
  useGetTtsUsageSummaryQuery,
  useGetTtsUsageLogsQuery,
} = ttsApi;
